import { ValidDate } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { emptyToUndefined } from "@/utils/format.util";
import { tenYearsEndMonth } from "../../date-time-picker/DateTimePicker";
import type { Profile } from "../data/profile";
import { useMemberProfile } from "../MemberProfileContext";
import { OwnedField } from "./FormFields";
import { FormShell } from "./ProfileOverlayShell";
import type { FormProps } from "./types";

const diningFormSchema = z.object({
    diningPreferences: z.string(),
    foodAllergies: z.string(),
    diningFuturePlans: z.string(),
});

type DiningFormValues = z.infer<typeof diningFormSchema>;

export function DiningForm({ onClose }: FormProps) {
    const { profile, saveDining } = useMemberProfile();
    const { control, handleSubmit } = useForm<DiningFormValues>({
        resolver: zodResolver(diningFormSchema),
        defaultValues: {
            diningPreferences: profile.dining.diningPreferences ?? "",
            foodAllergies: profile.dining.foodAllergies ?? "",
            diningFuturePlans: profile.dining.futurePlan?.text ?? "",
        },
    });

    function onSubmit(values: DiningFormValues) {
        saveDining(
            {
                diningPreferences: emptyToUndefined(values.diningPreferences),
                foodAllergies: emptyToUndefined(values.foodAllergies),
                diningFuturePlans: emptyToUndefined(values.diningFuturePlans),
            },
            onClose,
        );
    }

    return (
        <FormShell
            title="Edit dining"
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <OwnedField
                id="dining-preferences"
                label="Must know"
                kind="textarea"
                control={control}
                name="diningPreferences"
            />
            <OwnedField
                id="dining-allergies"
                label="Restrictions"
                kind="textarea"
                control={control}
                name="foodAllergies"
            />
            <OwnedField
                id="dining-future-plans"
                label="Future plans"
                kind="textarea"
                control={control}
                name="diningFuturePlans"
            />
        </FormShell>
    );
}

const travelFormSchema = z.object({
    travelMustKnow: z.string(),
    nationality: z.string(),
    passportName: z.string(),
    passportNumber: z.string(),
    passportExpiryDate: ValidDate.optional(),
    airlinePreferences: z.string(),
    frequentFlyerNumber: z.string(),
    travelFuturePlans: z.string(),
});

type TravelFormValues = z.infer<typeof travelFormSchema>;

const TRAVEL_IDENTITY_FIELDS: {
    name: "nationality" | "passportName" | "passportNumber";
    label: string;
}[] = [
    { name: "nationality", label: "Nationality" },
    { name: "passportName", label: "Passport name" },
    { name: "passportNumber", label: "Passport number" },
];

const TRAVEL_PREFERENCE_FIELDS: {
    name: "airlinePreferences" | "frequentFlyerNumber";
    label: string;
}[] = [
    { name: "airlinePreferences", label: "Airline preference" },
    { name: "frequentFlyerNumber", label: "Frequent flyer" },
];

export function TravelForm({ onClose }: FormProps) {
    const { profile, saveTravel } = useMemberProfile();
    const { control, handleSubmit } = useForm<TravelFormValues>({
        resolver: zodResolver(travelFormSchema),
        defaultValues: {
            travelMustKnow: profile.travel.travelMustKnow ?? "",
            nationality: profile.travel.nationality ?? "",
            passportName: profile.travel.passportName ?? "",
            passportNumber: profile.travel.passportNumber ?? "",
            passportExpiryDate: profile.travel.passportExpiryDate
                ? parseISO(profile.travel.passportExpiryDate)
                : undefined,
            airlinePreferences: profile.travel.airlinePreferences ?? "",
            frequentFlyerNumber: profile.travel.frequentFlyerNumber ?? "",
            travelFuturePlans: profile.travel.futurePlan?.text ?? "",
        },
    });

    function onSubmit(values: TravelFormValues) {
        saveTravel(
            {
                travelMustKnow: emptyToUndefined(values.travelMustKnow),
                nationality: emptyToUndefined(values.nationality),
                passportName: emptyToUndefined(values.passportName),
                passportNumber: emptyToUndefined(values.passportNumber),
                passportExpiryDate: values.passportExpiryDate
                    ? format(values.passportExpiryDate, "yyyy-MM-dd")
                    : undefined,
                airlinePreferences: emptyToUndefined(values.airlinePreferences),
                frequentFlyerNumber: emptyToUndefined(values.frequentFlyerNumber),
                travelFuturePlans: emptyToUndefined(values.travelFuturePlans),
            },
            onClose,
        );
    }

    return (
        <FormShell
            title="Edit travel"
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <OwnedField
                id="travel-must-know"
                label="Must know"
                kind="textarea"
                control={control}
                name="travelMustKnow"
            />
            {TRAVEL_IDENTITY_FIELDS.map((field) => (
                <OwnedField
                    key={field.name}
                    id={`travel-${field.name}`}
                    label={field.label}
                    kind="text"
                    control={control}
                    name={field.name}
                />
            ))}
            <OwnedField
                id="travel-passportExpiryDate"
                label="Passport expires"
                kind="date"
                control={control}
                name="passportExpiryDate"
                endMonth={tenYearsEndMonth()}
            />
            {TRAVEL_PREFERENCE_FIELDS.map((field) => (
                <OwnedField
                    key={field.name}
                    id={`travel-${field.name}`}
                    label={field.label}
                    kind="text"
                    control={control}
                    name={field.name}
                />
            ))}
            <OwnedField
                id="travel-future-plans"
                label="Future plans"
                kind="textarea"
                control={control}
                name="travelFuturePlans"
            />
        </FormShell>
    );
}

const mustKnowFormSchema = z.object({
    notes: z.string(),
});

type MustKnowFormValues = z.infer<typeof mustKnowFormSchema>;

export function MustKnowCategoryForm({
    section,
    onClose,
}: FormProps & { section: "events" | "wellness" | "shopping" }) {
    const { profile, saveEvents, saveWellness, saveShopping } = useMemberProfile();
    const { control, handleSubmit } = useForm<MustKnowFormValues>({
        resolver: zodResolver(mustKnowFormSchema),
        defaultValues: { notes: mustKnowValue(profile, section) },
    });

    function onSubmit(values: MustKnowFormValues) {
        const notes = emptyToUndefined(values.notes);
        switch (section) {
            case "events":
                saveEvents({ eventsAndExperiences: notes }, onClose);
                break;
            case "wellness":
                saveWellness({ wellnessMustKnow: notes }, onClose);
                break;
            case "shopping":
                saveShopping({ luxuryItemsAndCollectibles: notes }, onClose);
                break;
            default: {
                const _exhaustive: never = section;
                return _exhaustive;
            }
        }
    }

    return (
        <FormShell
            title={getFormTitle(section)}
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <OwnedField
                id={`${section}-must-know`}
                label="Must know"
                kind="textarea"
                control={control}
                name="notes"
            />
        </FormShell>
    );
}

function getFormTitle(section: "events" | "wellness" | "shopping"): string {
    switch (section) {
        case "events":
            return "Edit events";
        case "wellness":
            return "Edit wellness";
        case "shopping":
            return "Edit shopping";
        default: {
            const _exhaustive: never = section;
            return _exhaustive;
        }
    }
}

function mustKnowValue(
    profile: Profile,
    section: "events" | "wellness" | "shopping",
): string {
    switch (section) {
        case "events":
            return profile.events.eventsAndExperiences ?? "";
        case "wellness":
            return profile.wellness.wellnessMustKnow ?? "";
        case "shopping":
            return profile.shopping.luxuryItemsAndCollectibles ?? "";
        default: {
            const _exhaustive: never = section;
            return _exhaustive;
        }
    }
}
